import React, { useState, useCallback } from 'react';
import { Save, Trash2, Zap, CheckCircle } from 'lucide-react';
import Header from '../../components/ui/Header';
import WorkflowToolbar from './components/WorkflowToolbar';
import WorkflowCanvasEditor from './components/WorkflowCanvasEditor';
import TriggerConfigPanel from './components/TriggerConfigPanel';
import TemplateLibrary from './components/TemplateLibrary';
import WorkflowTestPanel from './components/WorkflowTestPanel';
import { useToast } from '../../components/ui/Toast';

const AutomationWorkflowDesigner = () => {
  const toast = useToast();
  const [nodes, setNodes] = useState([]);
  const [workflowName, setWorkflowName] = useState('Untitled Workflow');
  const [showTemplates, setShowTemplates] = useState(false);
  const [rightPanel, setRightPanel] = useState('trigger');
  const [saved, setSaved] = useState(false);

  const handleAddBlock = useCallback((block) => {
    setNodes(prev => [...prev, { ...block, instanceId: `${block?.id}_${Date.now()}` }]);
    toast?.success(`Added "${block?.label}" block to workflow.`, 'Block Added');
  }, [toast]);

  const handleAddBetween = useCallback((index) => {
    toast?.info('Select a block from the toolbar to insert it.', 'Insert Block');
  }, [toast]);

  const handleRemoveNode = useCallback((index) => {
    setNodes(prev => {
      const removed = prev?.[index];
      if (removed) toast?.info(`Removed "${removed?.label}" block.`, 'Block Removed');
      return prev?.filter((_, i) => i !== index);
    });
  }, [toast]);

  const handleConfigureNode = useCallback((node) => {
    toast?.info(`Configure "${node?.label}" — advanced config coming soon.`, 'Configure Block');
  }, [toast]);

  const handleUseTemplate = useCallback((template) => {
    if (!template?.nodes?.length) {
      toast?.error('Template has no nodes.', 'Empty Template');
      return;
    }
    setNodes(template?.nodes?.map(n => ({ ...n, instanceId: `${n?.id}_${Date.now() + Math.random()}` })));
    setWorkflowName(template?.name || 'Untitled Workflow');
    setShowTemplates(false);
    toast?.success(`Template "${template?.name}" loaded successfully!`, 'Template Loaded ✓');
  }, [toast]);

  const handleSave = useCallback(() => {
    if (!nodes?.length) {
      toast?.warning('Add at least one block before saving.', 'Empty Workflow');
      return;
    }
    setSaved(true);
    toast?.success(`Workflow "${workflowName}" saved successfully!`, 'Saved ✓');
    setTimeout(() => setSaved(false), 3000);
  }, [nodes, workflowName, toast]);

  const handleClear = useCallback(() => {
    if (nodes?.length > 0 && !window.confirm('Clear all blocks from the canvas?')) return;
    setNodes([]);
    setWorkflowName('Untitled Workflow');
    toast?.info('Canvas cleared.', 'Cleared');
  }, [nodes, toast]);

  const handleOpenTemplates = useCallback(() => {
    setShowTemplates(true);
  }, []);

  const handleTestTrigger = useCallback(() => {
    if (!nodes?.length) {
      toast?.warning('Add blocks to your workflow before testing.', 'Empty Workflow');
      return;
    }
    toast?.info('Trigger test initiated — check Test panel for results.', 'Test Started');
  }, [nodes, toast]);

  const handleCloseTemplates = useCallback(() => {
    setShowTemplates(false);
  }, []);

  const rightPanels = [
    { id: 'trigger', label: 'Trigger' },
    { id: 'test', label: 'Test' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <input
            type="text"
            value={workflowName}
            onChange={e => setWorkflowName(e?.target?.value)}
            className="text-base font-semibold text-gray-900 bg-transparent border-none outline-none focus:bg-gray-50 focus:px-2 rounded transition-all"
            placeholder="Workflow name..."
          />
          {saved && (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
              <CheckCircle className="w-3 h-3" /> Saved
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenTemplates}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Templates
          </button>
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Clear
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Save className="w-3.5 h-3.5" />
            Save
          </button>
        </div>
      </div>
      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Toolbar */}
        <WorkflowToolbar onAddBlock={handleAddBlock} onOpenTemplates={handleOpenTemplates} />

        {/* Canvas */}
        <div className="flex-1 overflow-auto">
          <WorkflowCanvasEditor
            nodes={nodes}
            onRemoveNode={handleRemoveNode}
            onConfigureNode={handleConfigureNode}
            onAddBetween={handleAddBetween}
          />
        </div>

        {/* Right Panel */}
        <div className="w-72 bg-white border-l border-gray-200 flex flex-col">
          <div className="flex border-b border-gray-200">
            {rightPanels?.map(panel => (
              <button
                key={panel?.id}
                onClick={() => setRightPanel(panel?.id)}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  rightPanel === panel?.id
                    ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {panel?.label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-auto">
            {rightPanel === 'trigger' && (
              <TriggerConfigPanel onTestTrigger={handleTestTrigger} />
            )}
            {rightPanel === 'test' && (
              <WorkflowTestPanel nodes={nodes} workflowName={workflowName} />
            )}
          </div>
        </div>
      </div>
      {/* Template Library Modal */}
      {showTemplates && (
        <TemplateLibrary
          onUseTemplate={handleUseTemplate}
          onClose={handleCloseTemplates}
        />
      )}
    </div>
  );
};

export default AutomationWorkflowDesigner;
